import React, { useState, useEffect, useRef } from "react";
import { GoogleMap, Marker, useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import { useTranslation } from "react-i18next";
import { fetchAddressFromCoords } from "../../../utils/methods/fetchAddressFromCoords";
import i18next from "i18next";


interface Props {
  lat: number | null;
  lng: number | null;
  onLocationChange: (lat: number, lng: number) => void;
}

const GoogleMapComponent: React.FC<Props> = ({ lat, lng, onLocationChange }) => {
  const { t } = useTranslation();
  const mapRef = useRef<google.maps.Map | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const [mapPosition, setMapPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [address, setAddress] = useState<string>("");

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyCs0NsympWsz3Lbxc7P3_pQTJZGjlbo0Ko",
    libraries: ["places"],
  });

  useEffect(() => {
    if (lat != null && lng != null) {
      const newPos = { lat, lng };
      setMapPosition(newPos);
      fetchAddress(newPos);
      mapRef.current?.panTo(newPos);
    }
  }, [lat, lng]);

  const fetchAddress = async ({ lat, lng }: { lat: number; lng: number }) => {
    try {
      const fetchedAddress = await fetchAddressFromCoords(lat, lng);
      setAddress(fetchedAddress);
    } catch (error) {
      console.error("Failed to fetch address:", error);
    }
  };

  const handleClick = (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;
    const newPos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    setMapPosition(newPos);
    onLocationChange(newPos.lat, newPos.lng);
    fetchAddress(newPos);
    mapRef.current?.panTo(newPos);
  };

  const handlePlaceChanged = () => {
    if (!autocompleteRef.current) return;
    const place = autocompleteRef.current.getPlace();
    if (!place.geometry?.location) return;

    const newPos = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };
    setMapPosition(newPos);
    onLocationChange(newPos.lat, newPos.lng);
    fetchAddress(newPos);
    mapRef.current?.panTo(newPos);
  };

  const handleCurrentLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((position) => {
      const newPos = { lat: position.coords.latitude, lng: position.coords.longitude };
      setMapPosition(newPos);
      onLocationChange(newPos.lat, newPos.lng);
      fetchAddress(newPos);
      mapRef.current?.panTo(newPos);
    });
  };

  if (!isLoaded || !mapPosition) return <p>{t("loading_map")}</p>;

  return (
    <div className="relative w-full h-[400px]"> {/* ارتفاع أكبر ليظهر زر Fullscreen */}
      <GoogleMap
        center={mapPosition}
        zoom={12}
        mapContainerStyle={{ width: "100%", height: "100%" }}
        onLoad={(map) => void (mapRef.current = map)}
        onClick={handleClick}
        options={{
          fullscreenControl: true, // الزر الأصلي يظهر
          zoomControl: true,
        }}
      >
        <Marker position={mapPosition} />
      </GoogleMap>

      {/* Overlay البحث وزر موقعي داخل الخريطة */}
      <div className={`absolute top-2 ${i18next.language==="ar"?"left-2":" right-2 left-2"} flex gap-2 bg-white p-2 w-[400px] rounded shadow z-10`}>
        <Autocomplete
          onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)}
          onPlaceChanged={handlePlaceChanged}
        >
          <input
            type="text"
            placeholder={t("search_location")}
            className="border  w-[320px]  p-1 rounded  outline-none focus:ring-1 focus:ring-primary"
          />
        </Autocomplete>
        <button
          onClick={handleCurrentLocation}
          type="button"
          className="bg-primary w-[50px] text-white px-2 rounded"
        >
          📍
        </button>
      </div>

      {/* عرض العنوان أسفل الخريطة */}
      {address && (
        <p className="absolute bottom-2 left-2 bg-white p-1 rounded shadow text-gray-700 z-10">
          {t("address")}: {address}
        </p>
      )}
    </div>
  );
};

export default GoogleMapComponent;

